import { addItem, delItem } from '../../redux/action/index';
import { sendRequest } from '../../ApiRequest';
import { BASE_URL } from '../../URL/constants';

// Regular function instead of a hook
// Track if checkout is in progress to prevent multiple navigations
let isCheckoutInProgress = false;

const CheckoutHandler = ({ 
  items = [], 
  onLoginRequired = null, 
  setShowLoginModal = null,
  navigate,
  dispatch,
  isLoggedIn
}) => {

  const addItemToCart = async (item) => {
    const currentUser = JSON.parse(localStorage.getItem("userDetails"));
    let cartState = JSON.parse(localStorage.getItem("cartState")) || {};
    
    // Ensure quantity is explicitly set and valid
    const itemQuantity = item.quantity && item.quantity > 0 ? item.quantity : 1;
    
    // CRITICAL: Get the actual product item_id, not the cart item ID
    // item.item_id is the actual product ID, item.id might be a guest cart ID
    const actualItemId = item.item_id || item.id;
    console.log('CheckoutHandler - addItemToCart called with item:', item, 'Quantity:', itemQuantity, 'Actual Item ID:', actualItemId);
    
    if (!currentUser) {
      // For guest users, check if item already exists and update quantity
      let guestCart = JSON.parse(localStorage.getItem("guestCart")) || [];
      
      // Handle both array and object formats
      let guestCartArray;
      if (Array.isArray(guestCart)) {
        guestCartArray = guestCart;
      } else {
        guestCartArray = Object.values(guestCart);
      }
      
      // Check if item already exists in cart
      const existingItemIndex = guestCartArray.findIndex(
        cartItem => cartItem.id === item.id || cartItem.item_id === item.id
      );
      
      // Remove from Redux if exists (to avoid duplicates)
      if (existingItemIndex !== -1) {
        dispatch(delItem(item));
      }
      
      // Remove from localStorage array
      guestCartArray = guestCartArray.filter(
        cartItem => cartItem.id !== item.id && cartItem.item_id !== item.id
      );
      
      // Add item with exact quantity
      guestCartArray.push({ ...item, quantity: itemQuantity });
      
      localStorage.setItem("guestCart", JSON.stringify(guestCartArray));
      dispatch(addItem({ ...item, quantity: itemQuantity }));
      cartState[item.id] = true;
      localStorage.setItem("cartState", JSON.stringify(cartState));
      window.dispatchEvent(new Event('cart-updated'));
      console.log('Guest cart updated with exact quantity:', itemQuantity);
      return true;
    }

    // For logged-in users, check if item exists in cart first
    const token = currentUser.data.token;
    const customerId = currentUser.data.customer.id;
    
    // Remove from Redux first to avoid duplicates
    dispatch(delItem(item));
    
    try {
      // First, check if item already exists in cart
      const cartResponse = await sendRequest(
        'GET',
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart`,
        null,
        { Authorization: token }
      );
      
      const cartItems = cartResponse.data?.cart_items || [];
      const existingCartItem = cartItems.find(
        cartItem => cartItem.item_id === actualItemId
      );
      
      if (existingCartItem) {
        // Delete existing cart item first, wait for it to complete
        try {
          await sendRequest(
            'DELETE',
            `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items/${actualItemId}`,
            null,
            { Authorization: token }
          );
          console.log('Existing cart item deleted, will add with exact quantity:', itemQuantity);
        } catch (deleteError) {
          console.log('Error deleting existing cart item:', deleteError);
          // Continue anyway, will try to add
        }
      }
    } catch (checkError) {
      console.log('Error checking existing cart:', checkError);
      // Continue to add item
    }
    
    // Add item to cart with exact quantity
    // Use actualItemId (product ID) not item.id (which might be guest cart ID)
    const cartData = {
      customer_id: customerId,
      item_id: actualItemId,
      quantity: itemQuantity
    };

    try {
      console.log('CheckoutHandler - Adding item to cart with EXACT quantity:', itemQuantity, 'CartData:', cartData);
      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items`,
        cartData,
        { Authorization: token }
      );

      if (response.data) {
        // Add to Redux with exact quantity (we already deleted old one)
        dispatch(addItem({ ...item, quantity: itemQuantity }));
        cartState[item.id] = true;
        localStorage.setItem("cartState", JSON.stringify(cartState));
        window.dispatchEvent(new Event('cart-updated'));
        console.log('Item added to cart successfully with EXACT quantity:', itemQuantity);
        return true;
      } else {
        console.error("Error adding to cart:", response.message);
        return false;
      }
    } catch (err) {
      console.error("API error while adding to cart:", err);
      // Fallback: add to Redux state with exact quantity
      dispatch(addItem({ ...item, quantity: itemQuantity }));
      cartState[item.id] = true;
      localStorage.setItem("cartState", JSON.stringify(cartState));
      window.dispatchEvent(new Event('cart-updated'));
      return true;
    }
  };

  const clearCart = async (currentUser, token, customerId) => {
    try {
      if (!currentUser) {
        // For guest users, clear localStorage cart
        const guestCart = JSON.parse(localStorage.getItem("guestCart")) || [];
        const guestCartArray = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
        
        // Clear Redux cart for all guest items
        guestCartArray.forEach(cartItem => {
          dispatch(delItem(cartItem));
        });
        
        // Clear localStorage
        localStorage.setItem("guestCart", JSON.stringify([]));
        localStorage.setItem("cartState", JSON.stringify({}));
        window.dispatchEvent(new Event('cart-updated'));
        console.log('Guest cart cleared successfully');
        return true;
      }
      
      // For logged-in users, clear API cart
      // Get all cart items first
      const cartResponse = await sendRequest(
        'GET',
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart`,
        null,
        { Authorization: token }
      );
      
      const cartItems = cartResponse.data?.cart_items || [];
      
      // Delete all existing cart items
      const deletePromises = cartItems.map(cartItem =>
        sendRequest(
          'DELETE',
          `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items/${cartItem.item_id}`,
          null,
          { Authorization: token }
        ).catch(err => {
          console.log('Error deleting cart item:', cartItem.item_id, err);
          return null; // Continue even if one fails
        })
      );
      
      await Promise.all(deletePromises);
      console.log('Cart cleared successfully');
      
      // Clear Redux cart
      cartItems.forEach(cartItem => {
        dispatch(delItem({ id: cartItem.item_id }));
      });
      
      // Clear localStorage cart state
      localStorage.setItem("cartState", JSON.stringify({}));
      window.dispatchEvent(new Event('cart-updated'));
      
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  const handleCheckout = async () => {
    // Prevent multiple simultaneous checkout attempts
    if (isCheckoutInProgress) {
      console.log('Checkout already in progress, ignoring duplicate call');
      return;
    }
    
    isCheckoutInProgress = true;
    
    try {
      if (!isLoggedIn) {
        // For guest users, ensure checkout items are in cart (don't clear cart)
        if (items.length > 0) {
          try {
            // Just ensure checkout items are in cart with correct quantities
            // Don't clear the cart - items should remain until payment is complete
            for (const item of items) {
              await addItemToCart(item);
            }
            
            // Show login modal
            if (onLoginRequired) {
              onLoginRequired();
            } else if (setShowLoginModal) {
              setShowLoginModal(true);
            } else {
              navigate('/login');
            }
            isCheckoutInProgress = false;
            return;
          } catch (error) {
            console.error('Error during guest checkout:', error);
            isCheckoutInProgress = false;
            return;
          }
        } else {
          // Show login modal if provided, otherwise use default behavior
          if (onLoginRequired) {
            onLoginRequired();
          } else if (setShowLoginModal) {
            setShowLoginModal(true);
          } else {
            navigate('/login');
          }
          isCheckoutInProgress = false;
          return;
        }
      }

      // Ensure checkout items are in cart with exact quantities
      // DO NOT clear the cart - items should remain until payment is successfully completed
      if (items.length > 0) {
        try {
          const currentUser = JSON.parse(localStorage.getItem("userDetails"));
          const token = currentUser.data.token;
          const customerId = currentUser.data.customer.id;
          
          console.log('CheckoutHandler - Processing checkout with items (NOT clearing cart):', items.map(i => ({ id: i.id, quantity: i.quantity })));
          
          // Save selected items to localStorage so CustomerInfoForm can use them
          // Use actual product item_id, not cart item id
          const selectedItemIds = items.map(item => {
            // Use item_id (product ID) if available, otherwise fallback to id
            // But prefer item_id as it's the actual product identifier
            return item.item_id || item.id;
          }).filter(Boolean);
          localStorage.setItem('checkoutSelectedItems', JSON.stringify(selectedItemIds));
          console.log('CheckoutHandler - Saved selected item IDs to localStorage:', selectedItemIds);
          
          // Just ensure checkout items are in cart with correct quantities
          // Don't clear the cart - it will be cleared only after payment success
          // Process items sequentially to avoid race conditions
          for (const item of items) {
            await addItemToCart(item);
          }
          
          // Wait a moment for Redux state to update, then navigate only once
          setTimeout(() => {
            if (isCheckoutInProgress) {
              // Check if we're not already on customer-info page
              const currentPath = window.location.pathname;
              if (currentPath !== '/customer-info') {
                navigate('/customer-info');
              }
              // Reset flag immediately after navigation
              isCheckoutInProgress = false;
            }
          }, 300);
        } catch (error) {
          console.error('Error during checkout:', error);
          // Still navigate even if cart update fails, but only once
          if (isCheckoutInProgress) {
            const currentPath = window.location.pathname;
            if (currentPath !== '/customer-info') {
              navigate('/customer-info');
            }
            isCheckoutInProgress = false;
          }
        }
      } else {
        // No items to add, just navigate once
        if (isCheckoutInProgress) {
          const currentPath = window.location.pathname;
          if (currentPath !== '/customer-info') {
            navigate('/customer-info');
          }
          isCheckoutInProgress = false;
        }
      }
    } catch (error) {
      console.error('Unexpected error in handleCheckout:', error);
      isCheckoutInProgress = false;
    }
  };

  return { handleCheckout, addItemToCart };
};

export default CheckoutHandler; 